import { AgGridModule } from '@ag-grid-community/angular';
import { RowClickedEvent, ValueFormatterParams } from '@ag-grid-community/core';
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { AccessManagementRoleRecord } from '@cdw-ae/common-data';
import { MatDialog } from '@cdw-ae/common-frontend-materials';
import {
  CdwConfirmationDialogComponent,
  CdwDialogModule,
  ConfirmationDialogResult,
} from '@cdw-ae/common-ui-components-dialog';
import { DrawerContent } from '@cdw-ae/common-ui-components-drawer-content';
import {
  CdwColDef,
  CdwSearchGridComponent,
  CdwSearchGridModule,
} from '@cdw-ae/common-ui-components-search-grid';
import { ErrorSnackbarComponent } from '@cdw-ae/components-error-snackbar';
import { FilterBarField } from '@cdw-ae/filter-bar-interfaces';
import { FilterBarTextMatchersModule } from '@cdw-ae/filter-bar-text-matchers';
import { take } from 'rxjs';
import { TailwindColors } from '../../../../../../common/constants/tailwind-colors';
import { RolesFilterSettings } from '../../../../../../common/services/route-config/configs/filter-bar-config.config';
import { EmptyStateOverlayComponent } from '../../../../../../components/empty-state-overlay/empty-state-overlay.component';
import { AccessManagementFrontendAuthService } from '../access-management-frontend-auth.service';
import { RolesDataService } from './roles-data.service';

@Component({
  selector: 'roles',
  standalone: true,
  imports: [
    CommonModule,
    AgGridModule,
    MatProgressSpinnerModule,
    FormsModule,
    ReactiveFormsModule,
    CdwSearchGridModule,
    CdwDialogModule,
    FilterBarTextMatchersModule,
    EmptyStateOverlayComponent,
  ],
  templateUrl: './roles.component.html',
  styleUrl: './roles.component.scss',
})
export class RolesComponent implements OnInit {
  public isLoading = false;
  // Temporary flag to show overlay
  showNoRolesOverlay = false;
  @ViewChild('roleGrid')
  searchGridComponent!: CdwSearchGridComponent<AccessManagementRoleRecord>;

  // HTML content for empty state overlay
  public rolesEmptyStateHtml = this.getEmptyStateHtml();
  public drawerContent: DrawerContent[] = [];
  public drawerContentTitle = '';
  public filterBarSettings!: FilterBarField[];
  public rolesSearchComponentId = 'rolesComponent';
  public readonly searchFieldMask: string[] = ['name', 'description'];

  @Output() updateDrawerContent =
    new EventEmitter<AccessManagementRoleRecord>();

  public columnDefs: CdwColDef[] = [
    {
      field: 'name',
      headerName: 'Role',
      width: 300,
      sort: 'asc',
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 600,
    },
    {
      field: 'roleType.type',
      headerName: 'Type',
      width: 150,
      valueFormatter: (params: ValueFormatterParams) => {
        const type = (params.data as AccessManagementRoleRecord)?.roleType
          ?.type;
        if (!type) return '';
        return `${type} Role`;
      },
    },
    {
      cellRenderer: 'deleteItemIconComponent',
      headerName: 'Delete',
      flex: 1,
    },
  ];

  public filterBySelectedColumnName = 'id';

  public itemLabel = 'role';

  // Theme colors for search grid
  public customTextColor: string = TailwindColors.gray300;
  public firstColumnTextColor: string = TailwindColors.tertiaryBlueLink;

  constructor(
    public rolesDataService: RolesDataService,
    private accessManagementAuthService: AccessManagementFrontendAuthService,
    private snackbar: MatSnackBar,
    private dialog: MatDialog,
    private router: Router
  ) {
    this.filterBarSettings = RolesFilterSettings.filter(
      (filterBarField: FilterBarField): boolean =>
        this.searchFieldMask.includes(filterBarField.name)
    );
  }

  async ngOnInit(): Promise<void> {
    this.isLoading = true;
    await this.accessManagementAuthService.setUserAuthContext();
  }

  private getCustomerId(): number | null {
    return this.accessManagementAuthService.authContext?.sqlCustomerId ?? null;
  }

  public onRowClicked(event: RowClickedEvent): void {
    this.emitUpdateDrawerContent(event);
  }

  deleteRoleClicked(event: {
    rowId: number | string;
    gridComponentId?: string;
  }) {
    const id = (event as unknown as { rowId: number }).rowId;
    const customerId = this.getCustomerId();
    if (!customerId) {
      this.snackbar.openFromComponent(ErrorSnackbarComponent, {
        data: 'Authentication context is not available.',
      });
      return;
    }

    // Check if role can be deleted before showing confirmation dialog
    this.rolesDataService
      .canDeleteRole(id)
      .pipe(take(1))
      .subscribe((canDeleteResponse) => {
        if (!canDeleteResponse.canDelete) {
          // Show error immediately without confirmation dialog
          this.snackbar.openFromComponent(ErrorSnackbarComponent, {
            data:
              canDeleteResponse.message ||
              'Error deleting role, it is assigned to a designation',
            duration: 6000,
          });
          return;
        }

        // Role can be deleted, show confirmation dialog
        const deleteDialogRef = this.dialog.open(
          CdwConfirmationDialogComponent,
          {
            data: {
              title: 'Confirmation',
              message: 'Are you sure you would like to delete this role?',
              type: 'warning',
              layout: 'warning-box',
              confirmText: 'Delete',
              cancelText: 'Cancel',
              confirmButtonColor: 'warn',
              hard: true,
            },
            width: '540px',
            maxWidth: '100%',
            disableClose: true,
            hasBackdrop: true,
          }
        );

        deleteDialogRef
          .afterClosed()
          .pipe(take(1))
          .subscribe(async (result: ConfirmationDialogResult | undefined) => {
            if (result?.confirmed && id) {
              const observable = await this.rolesDataService.deleteRole(id);
              observable
                .pipe(take(1))
                .subscribe((response: { success: boolean }) => {
                  if (response.success) {
                    this.snackbar.open('Role deleted successfully.', 'OK', { duration: 6000 });
                    this.refreshSearchGrid();
                  } else {
                    this.snackbar.openFromComponent(ErrorSnackbarComponent, {
                      data: 'Error deleting role, it is assigned to a designation',
                    });
                  }
                });
            }
          });
      });
  }

  onAddNewRole(): void {
    this.router.navigate(['administration/access-management/role/edit/new']);
  }

  getEmptyStateHtml(): string {
    return `No roles have been set up. Start by <a href="/administration/access-management/role/edit/new" class="empty-state-link">adding a new role</a>.`;
  }

  emitUpdateDrawerContent(event: { data: AccessManagementRoleRecord }) {
    this.updateDrawerContent.emit(event.data);
  }

  refreshSearchGrid() {
    this.searchGridComponent.refreshGrid();
  }

  onDataLoaded(): void {
    this.isLoading = false;
  }
}
